import React from 'react';
import { styled } from '../../styles';
import { useIntlBds } from '../../translations/BdsIntlProvider';
import { BpDesignSystem } from '../../types';
import { humanFileSize } from '../../utils';
import { Keys } from '../../utils/keysUtil';
import { Box } from '../Box/Box';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton';
import { Link } from '../Link';
import { LinearProgress } from '../Progress';
import { Typography } from '../Typography';
import { TypographyTooltip } from '../TypographyTooltip';

type Attachment = Partial<Pick<File, 'name' | 'size'>>;
type FileSize = number;
type Progress = number;

export type FileCardProps = BpDesignSystem.Props<{
  uploading?: boolean | Progress;
  readonly?: boolean;
  onDownload?(): void;
  onUploadAbort?(): void;
  onDelete?(): void;
  file: Attachment;
  completed?: boolean | FileSize;
}>;

export const FileCard: React.FC<FileCardProps> = ({
  uploading,
  file,
  completed,
  readonly,
  onDownload,
  onDelete,
  onUploadAbort,
  ...rest
}) => {
  const { intl } = useIntlBds();
  const state = {
    loading: uploading === true || typeof uploading === 'number',
    progress: typeof uploading === 'number' ? uploading : undefined
  };

  return (
    <StyledDocumentField
      dataTest={`download-${file.name}`}
      $readonly={readonly || !onDownload}
      $uploading={!!uploading}
      tabIndex={!readonly && !!onDownload ? 0 : -1}
      {...rest}
      onClick={!readonly && !state.loading && !!onDownload ? () => onDownload() : undefined}
      onKeyDown={
        !readonly && !state.loading && !!onDownload
          ? (e) => {
              if (Keys.oneOf(e, 'Enter', ' ')) {
                e.preventDefault();
                onDownload();
              }
            }
          : undefined
      }
    >
      {uploading ? (
        <StyledIcon>
          <Icon name="upload" />
        </StyledIcon>
      ) : (
        <StyledIcon>
          <Icon name="attach_file" />
        </StyledIcon>
      )}
      <StyledFileSection>
        <StyledFileName>
          <TypographyTooltip
            variant="body"
            dataTest="file-name"
            slotProps={{
              Tooltip: {
                placement: 'top-start'
              }
            }}
          >
            {file.name}
          </TypographyTooltip>
          {state.loading ? (
            <LinearProgress
              value={state.progress}
              variant={typeof state.progress === 'number' ? 'determinate' : 'indeterminate'}
            />
          ) : (
            <div>
              {!readonly &&
                (completed ? (
                  <StyledDownload variant="body" small>
                    <Icon color="colorSuccess" name="check_circle" size={16} />
                    <Link component="button" type="button" underline={readonly ? 'none' : 'hover'}>
                      {intl.DocumentField.completedButtonLabel}
                      {typeof completed === 'number' ? ` - ${humanFileSize(completed)}` : ''}
                    </Link>
                  </StyledDownload>
                ) : !!onDownload ? (
                  <StyledDownload variant="body" small>
                    <Icon color="colorPrimary" name="cloud_download" size={16} />
                    <Link component="button" type="button" underline={readonly ? 'none' : 'hover'}>
                      {intl.DocumentField.downloadButtonLabel}
                      {file?.size ? ` - ${humanFileSize(file.size)}` : ''}
                    </Link>
                  </StyledDownload>
                ) : null)}
            </div>
          )}
        </StyledFileName>
        {(onUploadAbort || onDelete) && (
          <StyledAbortDeleteButton
            dataTest="abort-upload-button"
            iconName="close"
            onKeyDown={(e) => {
              if (Keys.oneOf(e, 'Enter', ' ')) {
                e.preventDefault();
                e.stopPropagation();
                state.loading ? onUploadAbort?.() : onDelete?.();
              }
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              state.loading ? onUploadAbort?.() : onDelete?.();
            }}
            size="small"
          />
        )}
      </StyledFileSection>
    </StyledDocumentField>
  );
};

const StyledIcon = styled('div')`
  align-items: center;
  background-color: ${({ theme }) => theme.tokens.colorSurfaceVariant};
  border-radius: ${({ theme }) => theme.tokens.radiusDefault};
  display: flex;
  height: 40px;
  justify-content: center;
  width: 40px;
`;

const StyledAbortDeleteButton = styled(IconButton)`
  && {
    margin-left: ${({ theme }) => theme.spacingSize.xl};
  }
`;

const StyledDownload = styled(Typography)`
  align-items: center;
  display: flex;
  gap: 0.25rem;  
`;

const StyledDocumentField = styled(Box)<{ $uploading: boolean; $readonly?: boolean }>`
  transition: background-color 0.2s ease-in-out;
  
  :hover, :focus {
    background-color: ${({ theme, $readonly }) => (!$readonly ? theme.tokens.stateOnSurfaceHoverStateLayerColor : undefined)};
  }
  :active {
    background-color: ${({ theme, $readonly }) => (!$readonly ? theme.tokens.stateOnSurfacePressedStateLayerColor : undefined)};
  }

  align-items: center;
  border-radius: ${({ theme }) => theme.tokens.radiusDefault};
  border: 1px solid ${({ theme }) => theme.tokens.colorSurfaceVariant};
  cursor: ${({ $uploading, $readonly }) => ($uploading ? 'inherit' : !$readonly ? 'pointer' : 'default')};
  display: flex;
  gap: ${({ theme }) => theme.spacingSize.m};
  min-height: 64px;
  padding: ${({ theme }) => theme.spacingSize.m};
  width: 100%;
`;

const StyledFileSection = styled('div')`
  align-items: center;
  display: flex;
  flex: 1;
  min-width: 0;
`;

const StyledFileName = styled('div')`
  [role='progressbar'] {
    flex: 1;
  }
  
  overflow: hidden;
  flex: 1;
`;
